import { JSX } from "react";
import {
  Column_ColumnPinning,
  Column_ColumnSizing,
  flexRender,
  Row,
  RowData,
  Row_ColumnVisibility,
  TableFeatures,
} from "@tanstack/react-table";
import { VirtualItem } from "@tanstack/react-virtual";
import {
  ColumnGroupBoundaries,
  EMPTY_COLUMN_GROUP_BOUNDARIES,
} from "@app/shared/virtualized-table/getColumnGroupBoundaries";

interface VirtualizedTableRowProps<TFeatures extends TableFeatures, TData extends RowData> {
  ariaRowIndex: number;
  /** See getColumnGroupBoundaries — draws a vertical divider marking each column group's edges. */
  columnGroupBoundaries?: ColumnGroupBoundaries;
  row: Row<TFeatures, TData>;
  testId?: string;
  virtualRow: VirtualItem;
}

/**
 * One virtualized body row, absolutely positioned per the virtualizer's offset. Pinned cells read
 * their pixel offset directly from TanStack's column-pinning API — no DOM measurement needed.
 * role="row"/"cell" (not "gridcell") since virtualized-table consumers have no keyboard
 * interaction. Has no per-row context concept of its own — a caller that needs to provide context
 * to its cells (e.g. a shared toggle state) wraps this component in its own Provider instead.
 *
 * Positioned via `top`, not `transform: translateY(...)` — a CSS transform on an ancestor creates a
 * new containing block for `position: fixed` descendants, which can break a `position: fixed`
 * menu/dropdown rendered inside a cell (it computes viewport-relative coordinates via
 * getBoundingClientRect(), but a fixed descendant of a transformed row resolves those coordinates
 * against the row's untransformed box instead of the viewport). `top` avoids establishing that
 * containing block.
 *
 * Non-pinned cells get flexGrow: 1 so columns share leftover row width instead of leaving it blank
 * when few columns are visible — matches VirtualizedTableHeader's per-leaf flexGrow so column
 * edges stay aligned with the header above them. Pinned cells stay fixed (flexGrow: 0): their
 * sticky `left` offset is computed from fixed prior-column widths, so growing them would desync it.
 *
 * The `as typeof x & Feature_Xyz` casts below are load-bearing — see VirtualizedTableHeader's
 * doc comment for why an abstract `TFeatures` type parameter can't resolve feature-contributed
 * methods (`getVisibleCells`, `getIsPinned`, `getStart`, `getSize`) on its own.
 */
export const VirtualizedTableRow = <TFeatures extends TableFeatures, TData extends RowData>({
  ariaRowIndex,
  columnGroupBoundaries = EMPTY_COLUMN_GROUP_BOUNDARIES,
  row,
  testId,
  virtualRow,
}: VirtualizedTableRowProps<TFeatures, TData>): JSX.Element => (
  <div
    className="virtualized-table__row"
    style={{ height: virtualRow.size, top: virtualRow.start }}
    data-testid={testId}
    role="row"
    aria-rowindex={ariaRowIndex}
  >
    {(row as typeof row & Row_ColumnVisibility<TFeatures, TData>).getVisibleCells().map((cell, colIndex) => {
      const pinnedColumn = cell.column as typeof cell.column & Column_ColumnPinning & Column_ColumnSizing;
      const isPinned = pinnedColumn.getIsPinned() === "start";
      const isGroupStart = columnGroupBoundaries.groupStartIds.has(cell.column.id);
      const isGroupEnd = columnGroupBoundaries.groupEndIds.has(cell.column.id);
      return (
        <div
          key={cell.id}
          className={`virtualized-table__cell${isPinned ? " virtualized-table__cell--pinned" : ""}${
            isGroupStart ? " virtualized-table__cell--group-start" : ""
          }${isGroupEnd ? " virtualized-table__cell--group-end" : ""}`}
          style={{
            flexGrow: isPinned ? 0 : 1,
            left: isPinned ? pinnedColumn.getStart("start") : undefined,
            width: pinnedColumn.getSize(),
          }}
          role="cell"
          aria-colindex={colIndex + 1}
        >
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </div>
      );
    })}
  </div>
);
